/**
 * EntityConvergedShell — canonical modal body for the 7 non-Personal entity
 * dialogs (Business / Sole-Prop / Trust / Estate / Fiduciary / Government /
 * Special-Purpose). SPEC.md Addendum 6 §6.1/§6.4 locked design.
 *
 * Same shell as the Personal body (IndividualReviewBody): a STICKY 4-axis
 * status ribbon + jump-scroll pills, a DOCUMENTS landing rendered FIRST (the
 * entity's own captured-doc section — tile-grid capture), then every
 * other section stacked inline under a #pdwsec-<tab> anchor — nothing hidden,
 * no tab-swap, no 'review' pill (the inline sections ARE the review surface).
 * An IntersectionObserver lights the in-view pill. + the C4 major-conflict ALERT.
 *
 * Locked v6 (Bill 2026-06-11, Q3 approved) — the auto-classify ReviewDropStrip
 * is REMOVED from this shell: classifier-first capture is dead everywhere.
 * Entities capture via their converged tile grids (each dialog supplies its
 * own documents section, e.g. DocumentSlotsSection, in `sectionsByTab`).
 * Source: docs/operations/person-modal-inventory/06-INDIVIDUAL-SURFACE-
 * CLEANUP-MOCKUP.html + 03-DETERMINISTIC-TILES-DECISION.md.
 *
 * The dialog still owns `local` + save; it passes its section tree grouped by
 * tab (`sectionsByTab`) + a doc-capture sink (`onAttachedDocsChange`). Each
 * entity supplies its own tab SUBSET via `tabs`; we drop 'review', front-load
 * 'documents', and render the rest in the given order.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { RaoPerson } from '../../types';
import { PdwStatusBadges } from './PdwStatusBadges';
import { PdwTabStrip, type PdwTabKey, type PdwTabState } from './PdwTabStrip';
import {
  detectCrossDocConflicts,
  type CapturedDocForConflict,
  type ConflictFinding,
} from './captureStub';
import type { AttachedDoc } from './shared/attachedDocs';

export type EntityConvergedMode = 'review-add' | 'new' | 'edit';

export interface EntityConvergedShellProps {
  /** The live draft — drives badges + conflict detection. */
  person: RaoPerson;
  mode: EntityConvergedMode;
  /** Show the dup-in-cart banner. */
  dupInCart?: boolean;
  /** Tabs this entity surfaces (subset of the canonical 9). Must include 'review'. */
  tabs: ReadonlyArray<PdwTabKey>;
  /** Section content per tab. 'review' is synthesized (all sections stacked). */
  sectionsByTab: Partial<Record<PdwTabKey, ReactNode>>;
  /**
   * Locked v6 (Bill 2026-06-11, Q3) — the shell no longer hosts a capture
   * strip; these four props are RETAINED for dialog-caller compatibility
   * (every entity dialog passes them) but are unused here. Capture lives in
   * each entity's own documents section (e.g. DocumentSlotsSection), which
   * registers AttachedDocs with the dialog directly.
   */
  onAttachedDocsChange: (docs: ReadonlyArray<AttachedDoc>) => void;
  onPersonPatch?: (patch: Partial<RaoPerson>) => void;
  officerCode?: string;
  cartId?: string;
  readOnly?: boolean;
}

/** Build the cross-doc capture set from the draft's CIP fields (C4 input). */
function cipCaptures(person: RaoPerson): CapturedDocForConflict[] {
  const e = (person.extra ?? {}) as Record<string, unknown>;
  const s = (k: string): string => (typeof e[k] === 'string' ? (e[k] as string) : '');
  const out: CapturedDocForConflict[] = [];
  // The two CIP IDs share the entity contact's identity, so the cross-document
  // signal surfaced from the CIP fields is the ID number disagreeing between
  // ID1 and ID2 (BL's id-number-mismatch counterfeit rule).
  const idType = s('idType');
  const idType2 = s('idType2');
  if (idType || s('idNumber')) {
    out.push({ docId: 'GOVT_ID', label: 'Primary ID', idNumber: s('idNumber') });
  }
  // B3 (Bill 2026-06-03, §6.5) — only cross-compare ID NUMBERS when the two CIP
  // slots are the SAME document type. ID1 and ID2 are intentionally DIFFERENT
  // documents (e.g. Driver's License + SSN Card, or DL + Passport) whose numbers
  // differ BY NATURE; comparing across types raised a bogus "non-genuine ID"
  // major conflict that blocked the ID scan. Mirrors the Personal body's guard
  // (IndividualReviewBody) — this entity path was still missing it.
  if ((idType2 || s('idNumber2')) && idType2 !== '' && idType2 === idType) {
    out.push({ docId: 'GOVT_ID_2', label: '2nd ID', idNumber: s('idNumber2') });
  }
  return out;
}

export function EntityConvergedShell({
  person,
  dupInCart = false,
  tabs,
  sectionsByTab,
}: EntityConvergedShellProps): JSX.Element {
  // Addendum 6 §6.1 — NO 'review' pill (the inline sections ARE the review
  // surface). Documents leads (the landing); the rest of the entity's content
  // tabs follow in their given order. Each entity passes its own tab subset.
  const bodyTabs = useMemo<ReadonlyArray<PdwTabKey>>(
    () => tabs.filter((t) => t !== 'review' && t !== 'documents'),
    [tabs],
  );
  const navOrder = useMemo<ReadonlyArray<PdwTabKey>>(() => ['documents', ...bodyTabs], [bodyTabs]);
  const [activeTab, setActiveTab] = useState<PdwTabKey>('documents');

  const conflicts = useMemo<ReadonlyArray<ConflictFinding>>(
    () => detectCrossDocConflicts(cipCaptures(person)),
    [person],
  );
  const majorConflicts = useMemo(() => conflicts.filter((c) => c.severity === 'major'), [conflicts]);

  // Minimal tab-state (presence-based 'visited' dots); the dialog's own footer
  // gate is the authoritative completeness check.
  const tabState = useMemo<Partial<Record<PdwTabKey, PdwTabState>>>(() => {
    const st: Partial<Record<PdwTabKey, PdwTabState>> = {};
    for (const t of navOrder) {
      st[t] = { status: t === 'documents' || sectionsByTab[t] ? 'visited' : 'unvisited' };
    }
    return st;
  }, [navOrder, sectionsByTab]);

  // Addendum 6 §6.4 D-TABS-JUMP-SCROLL — all sections render at once under
  // #pdwsec-<tab> anchors; pills scroll to (never hide) their section; an
  // IntersectionObserver lights the in-view pill.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const jumpTo = useCallback((key: PdwTabKey): void => {
    setActiveTab(key);
    bodyRef.current?.querySelector(`#pdwsec-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    const sections = Array.from(root.querySelectorAll<HTMLElement>('[data-pdwsec]'));
    if (sections.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const key = visible ? (visible.target as HTMLElement).dataset['pdwsec'] : undefined;
        if (key) setActiveTab(key as PdwTabKey);
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.5, 1] },
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [navOrder]);

  return (
    <div ref={bodyRef} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {dupInCart && (
        <div role="alert" style={dupBanner}>
          CIF <strong>{person.cifId}</strong> is already on this cart — use Edit to update their record.
        </div>
      )}

      {majorConflicts.length > 0 && (
        <div role="alert" style={conflictBanner}>
          <strong>⚠ Document conflict — review required.</strong>{' '}
          {majorConflicts.map((c) => `${c.field} differs across ${c.docs.join(' / ')}`).join('; ')}
          . This may indicate a non-genuine ID — resolve before committing.
        </div>
      )}

      {/* §6.1/§6.4 + Step 5 — sticky 4-axis ribbon + jump-scroll pills. */}
      <div style={stickyHeader}>
        <PdwStatusBadges person={person} />
        <PdwTabStrip activeTab={activeTab} onTabChange={jumpTo} tabOrder={navOrder} tabState={tabState} />
      </div>

      {/* §6.1/§6.2 — DOCUMENTS landing FIRST: the captured-doc section the
          entity supplies (tile-grid capture; the auto-classify drop strip is
          removed per locked v6 Q3 — classifier-first capture is dead). */}
      <div id="pdwsec-documents" data-pdwsec="documents" style={secAnchor}>
        {sectionsByTab['documents'] ?? null}
      </div>

      {bodyTabs.map((t) => (
        <div key={t} id={`pdwsec-${t}`} data-pdwsec={t} style={secAnchor}>
          {sectionsByTab[t] ?? null}
        </div>
      ))}
    </div>
  );
}

const stickyHeader: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 5,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  paddingBottom: 8,
  marginBottom: -4,
  background: 'var(--surface-card,#fff)',
  borderBottom: '1px solid var(--ink-100,#f1f3f5)',
};
const secAnchor: React.CSSProperties = { scrollMarginTop: 124 };

const dupBanner: React.CSSProperties = {
  padding: '10px 14px',
  background: 'var(--warn-bg)',
  border: '1px solid var(--warn-line)',
  borderRadius: 6,
  fontSize: 13,
  color: 'var(--ink-900)',
};
const conflictBanner: React.CSSProperties = {
  padding: '10px 14px',
  background: 'var(--err-bg)',
  border: '1px solid var(--pcb-red, #b10505)',
  borderRadius: 6,
  fontSize: 13,
  color: 'var(--ink-900)',
};
